import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Upload, 
  X, 
  FileText, 
  CreditCard, 
  User,
  AlertCircle,
  CheckCircle,
  Loader2
} from 'lucide-react';
import { driverApplicationAPI } from '../../services/driverApplicationAPI';
import { DriverApplicationRequest } from '../../types';

interface DocumentUpload {
  file: File;
  type: string;
  preview?: string;
}

const DriverApplicationForm: React.FC = () => {
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const [formData, setFormData] = useState<DriverApplicationRequest>({
    license_number: '',
    license_expiry_date: '',
    emergency_contact_name: '',
    emergency_contact_phone: '',
    previous_experience: ''
  });

  const [documents, setDocuments] = useState<DocumentUpload[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'vehicle_year' ? parseInt(value) || new Date().getFullYear() : value
    }));
  };

  const handleFileUpload = (files: FileList | null) => {
    if (!files) return;

    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
    const maxSize = 5 * 1024 * 1024; // 5MB

    Array.from(files).forEach(file => {
      if (!allowedTypes.includes(file.type)) {
        setError('Only JPEG, PNG, and PDF files are allowed');
        return;
      }

      if (file.size > maxSize) {
        setError('File size must be less than 5MB');
        return;
      }

      const newDocument: DocumentUpload = {
        file,
        type: getDocumentType(file.name),
      };

      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setDocuments(prev => [...prev, {
            ...newDocument,
            preview: e.target?.result as string
          }]);
        };
        reader.readAsDataURL(file);
      } else {
        setDocuments(prev => [...prev, newDocument]);
      }
    });

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getDocumentType = (filename: string): string => {
    const lower = filename.toLowerCase();
    if (lower.includes('license') || lower.includes('licence')) return 'driving_license';
    if (lower.includes('id') || lower.includes('national')) return 'national_id';
    if (lower.includes('passport')) return 'passport';
    if (lower.includes('insurance')) return 'insurance';
    if (lower.includes('registration')) return 'vehicle_registration';
    return 'other';
  };

  const removeDocument = (index: number) => {
    setDocuments(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate required fields
      const requiredFields = [
        'license_number', 'license_expiry_date', 
        'emergency_contact_name', 'emergency_contact_phone'
      ];

      for (const field of requiredFields) {
        if (!formData[field as keyof DriverApplicationRequest]) {
          throw new Error(`${field.replace('_', ' ')} is required`);
        }
      }

      // Validate license expiry date
      const expiryDate = new Date(formData.license_expiry_date);
      if (expiryDate <= new Date()) {
        throw new Error('License expiry date must be in the future');
      }

      // Submit application
      const result = await driverApplicationAPI.submitApplication(
        formData,
        documents.map(doc => doc.file)
      );

      setSuccess(result.message);
      
      // Redirect to profile or dashboard after successful submission
      setTimeout(() => {
        navigate('/profile');
      }, 2000);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit application');
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentYear = new Date().getFullYear();
  // const years = Array.from({ length: 30 }, (_, i) => currentYear - i);

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg">
        {/* Header */}
        <div className="border-b border-gray-200 px-6 py-4">
          <h1 className="text-2xl font-bold text-gray-900">Apply to Become a Driver</h1>
          <p className="text-gray-600 mt-2">
            Complete this application to become a verified driver and start creating trips and routes.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-8">
          {/* Error/Success Messages */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center space-x-3">
              <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
              <span className="text-red-700">{error}</span>
            </div>
          )}

          {success && (
            <div className="bg-green-50 border border-green-200 rounded-md p-4 flex items-center space-x-3">
              <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
              <span className="text-green-700">{success}</span>
            </div>
          )}

          {/* Driver License Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <CreditCard className="h-5 w-5 mr-2" />
              Driver License Information
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="license_number" className="block text-sm font-medium text-gray-700 mb-2">
                  License Number *
                </label>
                <input
                  type="text"
                  id="license_number"
                  name="license_number"
                  value={formData.license_number}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="e.g., DL123456789"
                />
              </div>
              <div>
                <label htmlFor="license_expiry_date" className="block text-sm font-medium text-gray-700 mb-2">
                  License Expiry Date *
                </label>
                <input
                  type="date"
                  id="license_expiry_date"
                  name="license_expiry_date"
                  value={formData.license_expiry_date}
                  onChange={handleInputChange}
                  required
                  min={new Date().toISOString().split('T')[0]}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <User className="h-5 w-5 mr-2" />
              Emergency Contact
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="emergency_contact_name" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Name *
                </label>
                <input
                  type="text"
                  id="emergency_contact_name"
                  name="emergency_contact_name"
                  value={formData.emergency_contact_name}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Full name"
                />
              </div>
              <div>
                <label htmlFor="emergency_contact_phone" className="block text-sm font-medium text-gray-700 mb-2">
                  Contact Phone *
                </label>
                <input
                  type="tel"
                  id="emergency_contact_phone"
                  name="emergency_contact_phone"
                  value={formData.emergency_contact_phone}
                  onChange={handleInputChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="+1234567890"
                />
              </div>
            </div>
          </div>

          {/* Additional Information */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <FileText className="h-5 w-5 mr-2" />
              Additional Information
            </h2>
            <div className="space-y-6">
              <div>
                <label htmlFor="previous_experience" className="block text-sm font-medium text-gray-700 mb-2">
                  Previous Driving Experience
                </label>
                <textarea
                  id="previous_experience"
                  name="previous_experience"
                  value={formData.previous_experience}
                  onChange={handleInputChange}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Describe your driving experience, professional driving history, etc."
                />
              </div>
            </div>
          </div>

          {/* Document Upload */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Upload className="h-5 w-5 mr-2" />
              Required Documents
            </h2>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6">
              <div className="text-center">
                <Upload className="mx-auto h-12 w-12 text-gray-400" />
                <div className="mt-4">
                  <label htmlFor="documents" className="cursor-pointer">
                    <span className="mt-2 block text-sm font-medium text-gray-900">
                      Upload Documents
                    </span>
                    <span className="mt-1 block text-xs text-gray-500">
                      Driver's license, ID, vehicle registration, insurance (PDF, JPG, PNG up to 5MB each)
                    </span>
                  </label>
                  <input
                    ref={fileInputRef}
                    id="documents"
                    name="documents"
                    type="file"
                    multiple
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => handleFileUpload(e.target.files)}
                    className="sr-only"
                  />
                </div>
                <p className="mt-2 text-xs text-gray-500">or drag and drop</p>
              </div>
            </div>

            {/* Uploaded Documents */}
            {documents.length > 0 && (
              <div className="mt-4 space-y-3">
                <h3 className="text-sm font-medium text-gray-900">Uploaded Documents</h3>
                {documents.map((doc, index) => (
                  <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-md">
                    <div className="flex items-center space-x-3">
                      {doc.preview ? (
                        <img src={doc.preview} alt="Preview" className="h-10 w-10 object-cover rounded" />
                      ) : (
                        <FileText className="h-8 w-8 text-gray-400" />
                      )}
                      <div>
                        <p className="text-sm font-medium text-gray-900">{doc.file.name}</p>
                        <p className="text-xs text-gray-500">
                          {doc.type.replace('_', ' ')} • {(doc.file.size / 1024 / 1024).toFixed(1)} MB
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeDocument(index)}
                      className="text-red-600 hover:text-red-800"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit Button */}
          <div className="border-t border-gray-200 pt-6">
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
                <span>{isSubmitting ? 'Submitting...' : 'Submit Application'}</span>
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DriverApplicationForm;
